import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { jsonFetch } from '../api/http';
import { nativeApp, stashSharedContent, type NativeAppInfo, type TermDeckAppPlugin } from '../lib/native-app';
import { navigate } from '../lib/router';
import { useOwnerSurfaceAlert } from '../lib/owner-surface-alerts';
import { IconRefresh } from './icons';

interface AndroidRelease {
  available: boolean;
  versionName?: string;
  versionCode?: number;
  sha256?: string;
  downloadPath?: string;
}

/**
 * ECO.18 — o que o app Android precisa do term-deck web, montado uma vez depois do login:
 * - marca a página como app (`html.is-native-app`: áreas seguras, sem prompts de "instalar PWA");
 * - "Compartilhar → term-deck" leva o conteúdo ao compositor do Assistente (`/`), inclusive no app frio;
 * - aviso de versão nova do app, com um toque para baixar (SHA-256 conferido) e instalar.
 * No navegador não renderiza nada.
 */
export function NativeAppBridge(): JSX.Element | null {
  const [plugin] = useState<TermDeckAppPlugin | null>(() => nativeApp());
  const [update, setUpdate] = useState<{ info: NativeAppInfo; release: AndroidRelease } | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // #443 — a falha da atualização do app, enquanto aparece, vai à orquestração.
  useOwnerSurfaceAlert('app-update-error', plugin && update && !dismissed && error ? 'app_update_error' : null, error ?? undefined);

  useEffect(() => {
    if (!plugin) return;
    document.documentElement.classList.add('is-native-app');
    let alive = true;
    const receive = async (): Promise<void> => {
      try {
        const content = await plugin.takeSharedContent();
        if (!alive || !content.present) return;
        stashSharedContent(content);
        const here = window.location.pathname.replace(/\/+$/, '');
        if (here !== '' && here !== '/chat') navigate('/');
      } catch { /* nada compartilhado */ }
    };
    void receive();
    const handles: Array<{ remove: () => unknown }> = [];
    void Promise.resolve(plugin.addListener('sharedContent', () => void receive())).then(h => { if (alive) handles.push(h); else void h.remove(); });
    void Promise.resolve(plugin.addListener('updateProgress', (p) => {
      if (p.total > 0) setProgress(`baixando ${Math.round((p.done / p.total) * 100)}%`);
    })).then(h => { if (alive) handles.push(h); else void h.remove(); });

    void (async () => {
      try {
        const [info, release] = await Promise.all([plugin.info(), jsonFetch<AndroidRelease>('/api/app/android')]);
        if (alive && release.available && (release.versionCode ?? 0) > info.versionCode) setUpdate({ info, release });
      } catch { /* sem aviso de versão: o app segue funcionando */ }
    })();

    // ECO.24 — autodiagnóstico: a fila que o nativo gravou (abertura, carga, erros, travamentos) sobe para o
    // term-deck e só é apagada depois de o servidor confirmar os ids. Sem sessão ainda (401), fica para depois.
    const flushDiagnostics = async (): Promise<void> => {
      if (!plugin.pendingDiagnostics || !plugin.acknowledgeDiagnostics) return;
      try {
        const { events } = await plugin.pendingDiagnostics();
        if (!events.length) return;
        for (let i = 0; i < events.length; i += 50) {
          const batch = events.slice(i, i + 50);
          const r = await jsonFetch<{ ids: string[] }>('/api/app/android/diagnostics', { method: 'POST', body: JSON.stringify({ events: batch }) });
          await plugin.acknowledgeDiagnostics({ ids: r.ids });
        }
      } catch { /* sem rede ou sem sessão: a fila continua no aparelho */ }
    };
    void flushDiagnostics();
    const flushTimer = window.setInterval(() => void flushDiagnostics(), 60_000);
    void Promise.resolve(plugin.addListener('appResumed', () => void flushDiagnostics())).then(h => { if (alive) handles.push(h); else void h.remove(); });
    const onError = (e: ErrorEvent): void => {
      void plugin.recordDiagnostic?.({ kind: 'app_js_error', detail: `${e.message} (${(e.filename || '').split('/').pop()}:${e.lineno})`, where: window.location.pathname });
    };
    const onRejection = (e: PromiseRejectionEvent): void => {
      const reason = e.reason instanceof Error ? e.reason.message : String(e.reason);
      void plugin.recordDiagnostic?.({ kind: 'app_js_error', detail: `promessa rejeitada: ${reason}`, where: window.location.pathname });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      alive = false;
      window.clearInterval(flushTimer);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      for (const h of handles) void h.remove();
    };
  }, [plugin]);

  if (!plugin || !update || dismissed) return null;

  const install = async (): Promise<void> => {
    setError(null);
    setProgress('baixando…');
    try {
      await plugin.installUpdate({
        url: new URL(update.release.downloadPath ?? '/api/app/android/apk', window.location.origin).toString(),
        sha256: update.release.sha256,
      });
      setProgress('abrindo o instalador…');
    } catch (err) {
      setProgress(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="native-app-update" role="status" data-testid="native-app-update">
      <IconRefresh />
      <span>
        <b>Nova versão do app: {update.release.versionName}</b><small>você tem a {update.info.versionName}</small>
        {error ? <em>{error}</em> : null}
      </span>
      <button type="button" onClick={() => void install()} disabled={progress !== null && !error}>
        {progress ?? 'Atualizar'}
      </button>
      <button type="button" className="native-app-update-later" onClick={() => setDismissed(true)} aria-label="lembrar depois">
        depois
      </button>
    </div>
  );
}
